import {Shift} from '@domain/shifthandover'
import {FilterDatePicker} from '@hconnect/common/components/dateTimeAndShift/FilterDatePicker'
import {
  ShiftQuickSelectSlots,
  DateRange,
  StoppageKind,
  shifthandoverStoppagesList,
  DowntimeStoppageCode
} from '@hconnect/common/types'
import {Done as DoneIcon} from '@mui/icons-material'
import {Box, Button} from '@mui/material'
import {
  AddFilter,
  AllFilterOptions,
  EventTypeFilterDropDown,
  eventAndStoppageCodeTypes,
  StoppageCodeFilterDropDown,
  CategoryFilterDropDown,
  EquipmentFilters,
  FreeTextSearch,
  FunctionsFilterDropDown,
  MaintenanceNotificationFilterDropdown,
  PmNotificationStatusFilterDropDown,
  PrioritiesFilterDropDown,
  ProcessStageFilter,
  RecurrenceFilterDropDown,
  StatusFilterDropDown,
  WorkOrderStatusFilterDropDown,
  WorkOrderTypeFilterDropDown,
  FilterOptions,
  StoppageCodesMap
} from '@widgets/shift-event-filters'
import {intersection, isEmpty} from 'lodash'
import {Moment} from 'moment-timezone'
import React, {ReactNode, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../consts'

interface FilterParams<T> {
  isCollapsible: boolean
  settings: FilterOptions
  options: T[]
  defaultFilters: string[]
}

const getVisibleFilters = <T extends string>({
  isCollapsible,
  settings,
  options,
  defaultFilters
}: FilterParams<T>): string[] => {
  if (isCollapsible) {
    return [...defaultFilters, ...options]
  }
  return Object.keys(settings).reduce(
    (acc, key) => {
      if (settings[key] && defaultFilters.indexOf(key) === -1) {
        acc.push(key)
      }
      return acc
    },
    [...defaultFilters]
  )
}

type ShiftEventsFilterProps<T extends string> = {
  settings: AllFilterOptions
  onChange: (filterOptions: AllFilterOptions) => void
  isCollapsible: boolean
  applyFilter: (filter: FilterOptions) => void
  options: T[]
  defaultFilters?: string[]
  additionalActions?: ReactNode
  showDescriptions?: (show: boolean) => void
  plantId: string
  shifts: Shift[]
  timezone: string
  plantNow: Moment
  kilnStoppageCodes?: DowntimeStoppageCode[]
  rawMillStoppageCodes?: DowntimeStoppageCode[]
  finishMillStoppageCodes?: DowntimeStoppageCode[]
}

// TODO remove it
// eslint-disable-next-line complexity
export function ShiftEventsFilter<T extends string>({
  settings,
  onChange,
  isCollapsible,
  applyFilter,
  options,
  defaultFilters = [],
  additionalActions,
  showDescriptions,
  plantId,
  shifts,
  timezone,
  plantNow,
  kilnStoppageCodes = [],
  rawMillStoppageCodes = [],
  finishMillStoppageCodes = []
}: ShiftEventsFilterProps<T>) {
  const {
    t,
    i18n: {language}
  } = useTranslation(FEATURE_NAMESPACE)
  const [visibleFilters, setFilter] = useState<string[]>(
    getVisibleFilters({isCollapsible, settings, options, defaultFilters})
  )

  const handleTimeRangeChange = (value: ShiftQuickSelectSlots | DateRange) => {
    const next = {...settings, timeRange: value}
    if (isCollapsible) {
      onChange(next)
    } else {
      applyFilter(next)
    }
  }

  const removeFilter = (filterName: string) => {
    onChange({...settings, [filterName]: undefined})
    setFilter((prevFilters) => prevFilters.filter((pf) => pf !== filterName))
  }

  const selectAll = (filterName: string, filterOptions: unknown[]) => {
    onChange({...settings, [filterName]: filterOptions})
  }

  const allCodesMap = useMemo<StoppageCodesMap<DowntimeStoppageCode>>(
    () => ({
      kiln: kilnStoppageCodes,
      rawMill: rawMillStoppageCodes,
      cementMill: finishMillStoppageCodes
    }),
    [kilnStoppageCodes, rawMillStoppageCodes, finishMillStoppageCodes]
  )

  return (
    <Box
      data-test-id={'events-filter-bar-filter-options'}
      display={'flex'}
      sx={
        isCollapsible
          ? {
              flexDirection: 'column',
              paddingTop: '30px'
            }
          : {
              flexWrap: 'wrap',
              alignItems: 'center'
            }
      }
    >
      {visibleFilters.includes('timeRange') && (
        <FilterDatePicker
          onChange={handleTimeRangeChange}
          value={settings.timeRange}
          shifts={shifts}
          timezone={timezone}
          plantNow={plantNow}
          language={language}
          translate={t}
          defaultTimeRange="currentShift"
        />
      )}

      {visibleFilters.includes('processStage') && (
        <ProcessStageFilter
          onChange={(processStage) => onChange({...settings, processStage})}
          removeFilter={removeFilter}
          value={settings.processStage}
          selectAll={selectAll}
          isCollapsible={isCollapsible}
          plantId={plantId}
        />
      )}

      <EquipmentFilters
        visibleFilters={visibleFilters}
        removeFilter={removeFilter}
        plantId={plantId}
        equipment={settings.equipment}
        onEquipmentChange={(equipment) => onChange({...settings, equipment})}
        mainEquipment={settings.mainEquipment}
        onMainEquipmentChange={(mainEquipment) => onChange({...settings, mainEquipment})}
        functionalEquipment={settings.functionalEquipment}
        onFunctionalEquipmentChange={(functionalEquipment) =>
          onChange({...settings, functionalEquipment})
        }
        stoppedMainEquipment={settings.stoppedMainEquipment}
        onStoppedMainEquipmentChange={(stoppedMainEquipment) =>
          onChange({...settings, stoppedMainEquipment})
        }
      />

      {visibleFilters.includes('eventType') && (
        <EventTypeFilterDropDown
          data-test-id={'events-filter-bar-eventType'}
          onChange={(value) => onChange({...settings, eventType: value, stoppageCode: undefined})}
          value={settings.eventType}
          removeFilter={removeFilter}
          label={t('shiftEvent.label.eventType')}
          isCollapsible={isCollapsible}
          options={eventAndStoppageCodeTypes}
          name="eventType"
          selectAll={(selectedOptions) => {
            onChange({...settings, eventType: selectedOptions, stoppageCode: undefined})
          }}
        />
      )}

      {visibleFilters.includes('category') && (
        <CategoryFilterDropDown
          data-test-id={'events-filter-bar-category'}
          onChange={(value) => onChange({...settings, category: value})}
          value={settings.category}
          removeFilter={removeFilter}
          isCollapsible={isCollapsible}
          selectAll={selectAll}
        />
      )}

      {visibleFilters.includes('priority') && (
        <PrioritiesFilterDropDown
          data-test-id={'events-filter-bar-priority'}
          onChange={(value) => onChange({...settings, priority: value})}
          value={settings.priority}
          removeFilter={removeFilter}
          isCollapsible={isCollapsible}
          selectAll={selectAll}
        />
      )}

      {visibleFilters.includes('changeFunctions') && (
        <FunctionsFilterDropDown
          data-test-id={'events-filter-bar-changeFunctions'}
          onChange={(value) => onChange({...settings, changeFunctions: value})}
          value={settings.changeFunctions}
          removeFilter={removeFilter}
          isCollapsible={isCollapsible}
          selectAll={selectAll}
        />
      )}

      {visibleFilters.includes('status') && (
        <StatusFilterDropDown
          data-test-id={'events-filter-bar-status'}
          onChange={(value) => onChange({...settings, status: value})}
          value={settings.status}
          removeFilter={removeFilter}
          isCollapsible={isCollapsible}
          selectAll={selectAll}
        />
      )}

      {visibleFilters.includes('workOrderStatus') && (
        <WorkOrderStatusFilterDropDown
          data-test-id={'work-order-filter-bar-status'}
          onChange={(value) => onChange({...settings, workOrderStatus: value})}
          value={settings.workOrderStatus}
          removeFilter={removeFilter}
          isCollapsible={isCollapsible}
          selectAll={selectAll}
        />
      )}

      {visibleFilters.includes('orderType') && (
        <WorkOrderTypeFilterDropDown
          data-test-id={'work-order-filter-bar-type'}
          onChange={(value) => onChange({...settings, orderType: value})}
          value={settings.orderType}
          removeFilter={removeFilter}
          isCollapsible={isCollapsible}
          selectAll={selectAll}
        />
      )}

      {visibleFilters.includes('pmNotificationStatus') && (
        <PmNotificationStatusFilterDropDown
          data-test-id={'pm-notification-filter-bar-status'}
          onChange={(value) => onChange({...settings, pmNotificationStatus: value})}
          value={settings.pmNotificationStatus}
          removeFilter={removeFilter}
          isCollapsible={isCollapsible}
          selectAll={selectAll}
        />
      )}

      {visibleFilters.includes('repetition') && (
        <RecurrenceFilterDropDown
          data-test-id={'events-filter-bar-recurrence'}
          onChange={(value) => onChange({...settings, repetition: value})}
          value={settings.repetition}
          removeFilter={removeFilter}
          isCollapsible={isCollapsible}
          selectAll={selectAll}
        />
      )}

      {visibleFilters.includes('stoppageCode') && (
        <StoppageCodeFilterDropDown
          data-test-id={'events-filter-bar-stoppageCode'}
          onChange={(value) => {
            onChange({...settings, stoppageCode: value})
          }}
          value={settings.stoppageCode}
          stoppageKinds={
            intersection(settings.eventType, shifthandoverStoppagesList) as StoppageKind[]
          }
          stoppageCodesMap={allCodesMap}
        />
      )}

      {visibleFilters.includes('freeText') && (
        <FreeTextSearch
          data-test-id={'events-filter-bar-freeText'}
          onChange={(value) => {
            onChange({...settings, freeText: value})
          }}
          value={settings.freeText}
          showDescriptions={showDescriptions}
        />
      )}

      {visibleFilters.includes('maintenanceNotificationExists') && (
        <MaintenanceNotificationFilterDropdown
          data-test-id={'events-filter-bar-maintenanceNotificationExists'}
          id={'events-filter-bar-maintenanceNotificationExists'}
          onChange={(value) =>
            onChange({
              ...settings,
              maintenanceNotificationExists: value
            })
          }
          value={settings.maintenanceNotificationExists}
          removeFilter={removeFilter}
          isCollapsible={isCollapsible}
        />
      )}

      {!isEmpty([...options, ...defaultFilters]) && (
        <>
          {!isCollapsible ? (
            !isEmpty(options) ? (
              <>
                <AddFilter<T>
                  data-test-id={'events-filter-bar-add-filter'}
                  options={options}
                  translate={t}
                  onChange={(value: string[] = []) => {
                    return setFilter((prevFilters) => {
                      const filterIntersection = prevFilters.filter(
                        (x: string) => !value.includes(x)
                      )
                      const filterName = filterIntersection[0] as keyof FilterOptions
                      onChange({...settings, [filterName]: undefined})
                      return value
                    })
                  }}
                  values={visibleFilters}
                />
                {additionalActions}
              </>
            ) : (
              <></>
            )
          ) : (
            <Box m={1}>
              <Button
                fullWidth
                data-test-id={'events-filter-apply-filter'}
                onClick={() => applyFilter(settings)}
                variant="contained"
                color="primary"
                startIcon={<DoneIcon />}
              >
                {t('shiftEvent.label.applyFilter')}
              </Button>
            </Box>
          )}
        </>
      )}
    </Box>
  )
}
